import { useId } from 'react';

interface CodeInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * `readOnly`, never `disabled`, while a verification is in flight: a
   * disabled element is blurred by the browser, which collapses the mobile
   * keyboard and drops focus on `<body>` — and since this component never
   * unmounts between attempts, `autoFocus` cannot bring it back. Read-only
   * blocks edits while keeping focus and keyboard exactly where they are.
   */
  readOnly?: boolean;
  /** Lets the step put focus back after clearing a rejected code. */
  inputRef?: React.Ref<HTMLInputElement>;
  disabled?: boolean;
  error?: string | null;
  length?: number;
  /**
   * Announced on focus. WCAG 2.1 SC 3.2.2 (On Input) allows an input to change
   * context only if the user was told beforehand — and the last digit here
   * submits the form, so this is what tells them.
   */
  hint?: string;
}

/**
 * One-time-code entry. A single input (not per-digit boxes) so paste,
 * autofill and screen readers behave predictably; `autocomplete=one-time-code`
 * lets iOS/Android offer the SMS/mail code (docs/auth/35-design/accessibility.md).
 */
export function CodeInput({
  label,
  value,
  onChange,
  readOnly,
  inputRef,
  disabled,
  error,
  length = 6,
  hint,
}: CodeInputProps) {
  const id = useId();
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-buds-dark">
        {label}
      </label>
      <input
        id={id}
        ref={inputRef}
        value={value}
        readOnly={readOnly}
        disabled={disabled}
        onChange={(event) =>
          // Keep only digits so a pasted "123 456" still works.
          onChange(event.target.value.replace(/\D/g, '').slice(0, length))
        }
        inputMode="numeric"
        // A single input gives away none of the six-slot shape that per-digit
        // boxes show for free, so the placeholder carries the length instead.
        placeholder={'•'.repeat(length)}
        autoComplete="one-time-code"
        pattern="\d*"
        maxLength={length}
        autoFocus
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        className={[
          'mt-2 h-14 w-full rounded-xl border bg-buds-surface px-4',
          // The indent cancels the trailing letter-space after the last digit,
          // which otherwise pushes the whole code visibly left of centre.
          'text-center font-mono text-2xl tracking-[0.4em] indent-[0.4em] text-buds-dark',
          'placeholder:text-buds-muted/70',
          'focus-ring',
          'disabled:opacity-60',
          error ? 'border-buds-danger' : 'border-buds-muted/80',
        ].join(' ')}
      />
      {error ? (
        <p id={errorId} className="mt-2 text-sm text-buds-danger">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-2 text-sm leading-relaxed text-buds-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
